import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { WizardState, WizardStep } from '../api';
import { WizardShell } from '../wizard/WizardShell';
import { StepSelf } from '../wizard/StepSelf';
import { StepParties } from '../wizard/StepParties';
import { StepAccounts } from '../wizard/StepAccounts';
import { StepOpeningBalances } from '../wizard/StepOpeningBalances';
import { StepDone } from '../wizard/StepDone';

export function WizardPage({ wizard }: { wizard: WizardState }) {
  const [activeStep, setActiveStep] = useState<WizardStep | null>(null);
  const nav = useNavigate();

  useEffect(() => {
    setActiveStep((prev) => prev ?? wizard.currentStep);
  }, [wizard]);

  const step = activeStep ?? wizard.currentStep;

  return (
    <WizardShell
      wizard={wizard}
      activeStep={step}
      onStepSelect={setActiveStep}
      extra={
        <div className="space-y-2 text-[12px] leading-5 text-ink-muted">
          {wizard.completed && (
            <button
              type="button"
              className="inline-flex min-h-[40px] items-center rounded-full border border-border/70 bg-surface px-3.5 py-2 font-medium text-[var(--sec-accounts)] shadow-sm backdrop-blur transition-colors hover:bg-surface-raised"
              onClick={() => nav('/batches')}
            >
              Go to batches
            </button>
          )}
          <div>
            <Link to="/batches" className="font-medium text-ink hover:text-[var(--sec-accounts)]">
              Skip for now
            </Link>
          </div>
        </div>
      }
    >
      {step === 'self' && <StepSelf wizard={wizard} />}
      {step === 'uplines' && (
        <StepParties
          role="upline"
          title="Step 2 — Upline parties"
          description="Add the parties you receive weekly P&L sheets from. For most operators this starts with SRB."
          hint="Exact casing is preserved; matching still stays case-insensitive internally."
          minCount={1}
        />
      )}
      {step === 'clients' && (
        <StepParties
          role="client"
          title="Step 3 — Client parties"
          description="Add the people or POCs who hold accounts through you."
          hint="For example: Gori Bhaiya, Vikky Bhai, Victor Bhai, or Anshu."
          minCount={1}
        />
      )}
      {step === 'accounts' && <StepAccounts />}
      {step === 'opening_balances' && <StepOpeningBalances />}
      {step === 'done' && <StepDone wizard={wizard} />}
    </WizardShell>
  );
}
